import { BalanceOfAbi, FrankencoinSavingsReadAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Frankencoin savings module — two calls:
 *   1. `ZCHF.balanceOf(module)` → the whole book
 *   2. `module.currentRatePPM()` → identity guard (see below)
 *
 * ## Why not the generic 4626 reader
 *
 * The module is not a share token, and not a token at all: `asset()`,
 * `totalAssets()`, `totalSupply()` and `convertToAssets()` do not exist.
 * A depositor's position is an internal account (`savings(address)`)
 * holding a `saved` amount that grows in place as interest is booked —
 * there is nothing to price, so **`exchangeRate` is the constant 1e18**
 * and `totalSupply` is by definition `totalAssets`. This is the same
 * shape as [nativeWnlp](./nativeWnlp.ts) / [navOracle](./navOracle.ts)
 * (bespoke, `isErc4626: false`), except even simpler: the accrual shows
 * up as a growing balance rather than a growing rate, which is why the
 * registry entry is `isRebasing: true`.
 *
 * ## totalAssets
 *
 * `ZCHF.balanceOf(module)` — deposits stay in the module (they are
 * never lent out or rehypothecated; the docs call it legal segregation
 * via internal accounting), and interest is minted into it on `refresh`
 * via `ZCHF.coverLoss`. So the balance equals the sum of every account's
 * booked `saved`, up to two second-order effects, both immaterial and
 * both erring *low*: interest accrued but not yet booked is not minted
 * yet (~25 ZCHF against 12.17M at integration, i.e. 2e-4 %), and a
 * direct ZCHF transfer to the module would be counted without belonging
 * to anyone.
 *
 * ## The rate call is a guard, not data
 *
 * The APR comes from the intrinsic-yield fetcher, not from here — the
 * reader has nowhere to put it. It is still worth a slot: `balanceOf`
 * succeeds against *any* address, so pinning the wrong module (the
 * retired `0x3BF301B0…`, a bridged deployment, an EOA) would publish a
 * confident, wrong row rather than fail. Requiring `currentRatePPM()`
 * to decode proves the address really is a savings module. A *zero*
 * rate is not a failure: it only means new deposits are refused
 * (`ModuleDisabled`), while existing balances remain fully
 * withdrawable, so the entry must survive it.
 *
 * ## Liquidity
 *
 * The registry entry is `instant`, so `fetchPublic` reports the full
 * `totalAssets` as withdrawable — which is exact here: `withdraw` pays
 * out of the module's own balance and can never be short. The catch is
 * on the yield, not the liquidity: `INTEREST_DELAY` is 3 days and a
 * top-up re-weights the account's tick counter, so an early exit can
 * realise ~0 % (documented on the registry entry).
 */
export const readerFrankencoinSavings = (
  entry: SavingsRegistryEntry,
): SavingsReader => ({
  calls: [
    { address: entry.underlying, name: 'balanceOf', params: [entry.address] },
    { address: entry.address, name: 'currentRatePPM', params: [] },
  ],
  abis: [BalanceOfAbi, FrankencoinSavingsReadAbi],
  parse: ([balance, ratePPM]) => {
    const deposits = toBigInt(balance)
    if (deposits === undefined) return undefined
    // Identity guard — a reverted/undecodable rate means this is not a
    // savings module, so the balance we just read is not a savings book.
    if (toBigInt(ratePPM) === undefined) return undefined

    return {
      totalAssets: deposits,
      // No shares exist; the "supply" IS the deposited principal, and
      // the rate is fixed at par. Both are load-bearing downstream:
      // `fetchPublic` derives `convertToAssets` / `convertToShares` from
      // `exchangeRate`, and 1e18 makes them the identity.
      totalSupply: deposits,
      exchangeRate: ONE_E18,
    }
  },
})
